import { Link } from "react-router-dom";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import { ONET_ATTRIBUTION, ONET_URL } from "@/lib/riasec";
import { OnetBadge } from "@/components/OnetBadge";
import { Database, Compass, Layers, ShieldCheck, ExternalLink, BookOpen } from "lucide-react";

const RIASEC = [
  { k: "Realistic", d: "Hands-on, practical work with tools, machines, plants, or animals." },
  { k: "Investigative", d: "Working with ideas — research, analysis, and problem-solving." },
  { k: "Artistic", d: "Creative, open-ended expression through design, writing, or performance." },
  { k: "Social", d: "Helping, teaching, and working directly with people." },
  { k: "Enterprising", d: "Leading, persuading, and starting things — business and initiative." },
  { k: "Conventional", d: "Working with data, detail, and structure in an organized way." },
];

const SOURCES = [
  {
    icon: Compass, title: "O*NET Interest Profiler (RIASEC)",
    body: "Career interest scores and matched occupations come from the official O*NET Interest Profiler, delivered live through the O*NET Web Services API operated by the National Center for O*NET Development for the U.S. Department of Labor, Employment & Training Administration (USDOL/ETA).",
    link: { href: "https://www.onetcenter.org/IP.html", label: "About the O*NET Interest Profiler" },
  },
  {
    icon: Database, title: "O*NET OnLine occupations",
    body: "Occupation summaries linked from your Careers to Explore open on O*NET OnLine, the public database of standardized occupational information maintained by USDOL/ETA.",
    link: { href: "https://www.onetonline.org/", label: "O*NET OnLine" },
  },
  {
    icon: Layers, title: "Transferable Skills Benchmark (TSB)",
    body: "The 10-skill TSB is a separate model informed by durable-skills research (e.g., America Succeeds' Durable Skills framework) and mapped to coursework outcomes. Your RIASEC interests are never combined with your TSB scores.",
    link: null,
  },
];

export default function DataSources() {
  return (
    <MarketingPage
      eyebrow="Data & Attribution"
      title="Where GPA+ career data comes from."
      lead="GPA+ pairs its own Transferable Skills Benchmark with authoritative, publicly-maintained career data. We're transparent about every source — and we keep interests and skills clearly separate."
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {SOURCES.map((s) => (
          <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6 card-elevate" data-testid="data-source-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6">
              <s.icon className="h-5 w-5 text-[#003057]" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-[#003057]">{s.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
            {s.link && (
              <a href={s.link.href} target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#5fa114] hover:underline">
                {s.link.label} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ))}
      </div>

      {/* RIASEC explainer */}
      <div className="mt-16">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-[#78be21]" />
          <h2 className="font-heading text-2xl font-bold text-[#003057]">The six RIASEC interest areas</h2>
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
          The O*NET Interest Profiler measures six broad areas of vocational interest — the RIASEC model
          (Holland). These describe the <span className="font-semibold">kinds of work you enjoy</span>, which is
          different from the <span className="font-semibold">skills you've built</span> (your TSB).
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RIASEC.map((r) => (
            <div key={r.k} className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="font-heading text-base font-bold text-[#003057]">{r.k}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Separation callout */}
      <div className="mt-16 rounded-2xl border-t-4 border-[#005291] bg-slate-50 p-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-[#005291]" />
          <h2 className="font-heading text-2xl font-bold text-[#003057]">Interests and skills stay separate</h2>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-slate-700">
          Your Career Interest Profile (RIASEC) is a measure of interest, not ability. GPA+ never merges it into
          your Transferable Skills Benchmark, never displays it on the Skill Radar, and never sends your personal
          information to O*NET. Interest results are private by default — you choose whether to share them
          read-only with an advisor, and you can revoke that access at any time.
        </p>
      </div>

      {/* Attribution */}
      <div className="mt-10 rounded-2xl bg-[#003057] p-8 text-white" data-testid="onet-attribution-block">
        <h2 className="font-heading text-lg font-bold">O*NET attribution</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">{ONET_ATTRIBUTION}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <OnetBadge onDark data-testid="onet-badge-datasources" />
          <a href={ONET_URL} target="_blank" rel="noreferrer" data-testid="onet-webservices-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#003057] hover:bg-[#78be21] hover:text-white">
            Visit O*NET Web Services
          </a>
        </div>
      </div>

      <div className="mt-10 text-center text-sm text-slate-500">
        Questions about how we handle your data?{" "}
        <Link to="/privacy" className="font-semibold text-[#5fa114] hover:underline">See Privacy & Compliance</Link>.
      </div>
    </MarketingPage>
  );
}
